import moment from "moment";
import { ClassroomDetails, SchedulePeriod } from "@/stores/classroomStore_I";
import useStore from "@/stores";
import { getClassroomStore } from "stores/classroomStore";

/** Given an endTime in integer format convert it to number of seconds from now (which corresponds to classexpiry) */
export const convertEndTimeToSecsTillExpiry = (endTimeInt: number): number => {
	const currentDateTime = new Date();
	const currentMinute = currentDateTime.getMinutes();
	const currentHour = currentDateTime.getHours();

	const endTimeHour = Math.floor(endTimeInt / 100);
	const endTimeMinute = endTimeInt % 100;

	return (endTimeHour - currentHour) * 3600 + (endTimeMinute - currentMinute) * 60;
};

/**
 * Converts a unix timestamp to a formatted time string with proper zero-padding.
 * @param expiry - Unix timestamp in seconds, or undefined/null for no expiry
 * @returns Formatted time string or null if expiry is falsy
 */
const formatUnixTimeToString = (expiry?: number | null): string | null => {
	if (!expiry) return null;

	const unixMoment = moment.unix(expiry);
	let meridianTime = unixMoment.format("LT");

	// Add leading zero for single-digit hours
	if (meridianTime[1] === ":") {
		meridianTime = "0" + meridianTime;
	}

	return meridianTime;
};

/**
 * Generates a class expiry string based on the provided expiry time.
 * @param expiry
 * @param verbose
 * @param isMonitoring
 */
export const generateClassExpiryStr = (expiry: number, verbose = false, isMonitoring = false): string | null => {
	const meridianTime = formatUnixTimeToString(expiry);
	if (!meridianTime) return null;

	return verbose ? `${!isMonitoring ? "Class" : "Monitoring"} until ${meridianTime}` : meridianTime;
};

/**
 * Generates a class expiry string based on the provided expiry time for the tile on the overview page.
 * @param expiry - Unix timestamp in seconds, or undefined/null for no expiry
 */
export const generateClassEndsStr_tile = (expiry?: number | null): string | null => {
	const meridianTime = formatUnixTimeToString(expiry);
	if (!meridianTime) return null;

	return `Ends ${meridianTime}`;
};

/**
 * Generates session status text based on class end time and monitoring status.
 * @param classroomId - ID of the classroom
 * @param verbose - Whether to use verbose format
 */
export const generateSessionStatusTxt = (classroomId: string, verbose = false): string | null => {
	const classroomData = getClassroomStore(useStore.getState()).getClassroomV2(classroomId);
	const isMonitoring = classroomData.session?.isMonitoring;
	const classEndTime = getClassEndTimeUnix(classroomData);

	if (classEndTime === null) {
		return null;
	}

	return generateClassExpiryStr(classEndTime, verbose, isMonitoring);
};

/**
 * Iterates through a list of periods, returns the first matching schedule if found, otherwise returns null.
 * @param {array} periods array of period objects
 * @returns {number} class expiry in unix format (seconds since epoch)
 */
export const findUnixExpiryFromPeriods = (periods: SchedulePeriod[]): number | null => {
	if (periods) {
		const DAYS_LOOKUP = ["sun", "mon", "tue", "wed", "thur", "fri", "sat"];

		for (const period of periods) {
			const now = moment();
			const momentDay = now.day();

			if (period.day === DAYS_LOOKUP[momentDay]) {
				const now24hour = Number.parseInt(now.format("HHmm"));
				if (now24hour >= period.startTime && now24hour < period.endTime) {
					const endTimeStr = period.endTime.toString().padStart(4, "0");
					const endHourNumber = Number.parseInt(endTimeStr.slice(0, 2));
					const endMinuteNumber = Number.parseInt(endTimeStr.slice(2));
					return moment({
						hour: endHourNumber,
						minute: endMinuteNumber,
					}).unix();
				}
			}
		}
	}
	return null;
};

/**
 * Iterates through a list of periods, returns the first matching schedule start time if found, otherwise returns null.
 * @param {array} periods array of period objects
 * @returns {number} class start time in unix format (seconds since epoch)
 */
export const findUnixStartTimeFromPeriods = (periods: SchedulePeriod[]): number | null => {
	if (periods) {
		const DAYS_LOOKUP = ["sun", "mon", "tue", "wed", "thur", "fri", "sat"];

		for (const period of periods) {
			const now = moment();
			const momentDay = now.day();

			if (period.day === DAYS_LOOKUP[momentDay]) {
				const now24hour = Number.parseInt(now.format("HHmm"));

				if (now24hour >= period.startTime && now24hour < period.endTime) {
					const startTimeString = period.startTime.toString().padStart(4, "0");

					const startHourNumber = Number.parseInt(startTimeString.slice(0, 2));
					const startMinuteNumber = Number.parseInt(startTimeString.slice(2));

					return moment({
						hour: startHourNumber,
						minute: startMinuteNumber,
					}).unix();
				}
			}
		}
	}
	return null;
};

/**
 * Determines the class end time based on the classroom details.
 * If the class is in monitoring mode, it checks the session end time.
 * If not in monitoring mode, it checks the session end time and falls back to schedule periods if necessary.
 * @param {ClassroomDetails} classroomDetails - The details of the classroom.
 * @returns {number | null} The class end time in unix format (seconds since epoch) or null if not found.
 */
export const getClassEndTimeUnix = (classroomDetails: ClassroomDetails): number | null => {
	const sessionEndTime = classroomDetails.session?.sessionEndTime;
	const isMonitoring = classroomDetails.session?.isMonitoring;
	if (isMonitoring) {
		if (sessionEndTime && sessionEndTime > new Date().getTime() / 1000) {
			return sessionEndTime;
		}
	} else {
		if (sessionEndTime && sessionEndTime > new Date().getTime() / 1000) {
			return sessionEndTime;
		} else {
			//not all class types have schedules
			if (classroomDetails.schedule && classroomDetails.schedule.periods) {
				return findUnixExpiryFromPeriods(classroomDetails.schedule.periods);
			}
		}
	}
	return null;
};

/**
 * Determines the class start time based on the classroom details.
 * If the class is in monitoring mode, it checks the session start time.
 * If not in monitoring mode, it checks the session start time and falls back to schedule periods if necessary.
 * @param {ClassroomDetails} classroomDetails - The details of the classroom.
 * @returns {number | null} The class start time in unix format (seconds since epoch) or null if not found.
 */
export const getClassStartTimeUnix = (classroomDetails: ClassroomDetails): number | null => {
	if (classroomDetails !== undefined && Object.entries(classroomDetails).length > 0) {
		const currentTime = new Date().getTime() / 1000;
		const sessionStartTime = classroomDetails.session?.sessionStartTime;
		if (classroomDetails.session?.isMonitoring) {
			if (sessionStartTime && sessionStartTime < currentTime) {
				return sessionStartTime;
			}
			return null;
		} else {
			if (sessionStartTime && sessionStartTime < currentTime) {
				return sessionStartTime;
			}

			if (classroomDetails.schedule) {
				const unixStartTime = findUnixStartTimeFromPeriods(classroomDetails.schedule?.periods);

				if (unixStartTime) {
					return unixStartTime;
				}
			}
		}
	}
	return null;
};

export type ClassSessionType = "MANUAL" | "SCHEDULED";

/**
 * Determines the class session type based on the classroom details.
 * @param classroomDetails
 */
export const getClassSessionType = (classroomDetails: ClassroomDetails): ClassSessionType | null => {
	if (classroomDetails !== undefined && Object.entries(classroomDetails).length > 0) {
		const currentTime = new Date().getTime() / 1000;
		const sessionStartTime = classroomDetails.session?.sessionStartTime;
		if (classroomDetails.session?.isMonitoring) {
			if (sessionStartTime && sessionStartTime < currentTime) {
				return "MANUAL";
			}
			return null;
		} else {
			if (sessionStartTime && sessionStartTime < currentTime) {
				return "MANUAL";
			}

			if (classroomDetails.schedule) {
				const unixStartTime = findUnixStartTimeFromPeriods(classroomDetails.schedule?.periods);

				if (unixStartTime) {
					return "SCHEDULED";
				}
			}
		}
	}
	return null;
};
